"use client";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import Header from "../home/Header";
import slider1 from "@/assets/image/slider1.jpg";
import Footer from "../home/Footer";

const AboutContainer = styled.div`
  padding: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: #f9f9f9;
  position: relative;
  width: 100vw;
  height: 100vh;

  overflow-y: auto;

  scrollbar-width: none;

  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0; 
    height: 0;
    background: transparent;
  }
`;

const HeroSection = styled.div<{ background: string }>`
  position: relative;
  height: 100vh;
  min-height: 600px;
  width: 100%;
  overflow: hidden;
  background-image: url(${(props) => props.background});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background-attachment: fixed;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(2, 2, 2, 0.4), rgba(0, 0, 0, 0.8));
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 80vh;
    min-height: 500px;
    background-attachment: scroll;
  }
`;

const ContentContainer = styled.div`
  padding: 80px 10%;
  position: relative;
  background: white;

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const GradientAccent = styled.div`
  height: 8px;
  width: 100%;
  background: linear-gradient(135deg, #635bff, #8a84ff);
  position: absolute;
  top: 0;
  left: 0;
`;

const Section = styled(motion.section)`
  margin-bottom: 100px;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Title = styled.h1`
  font-size: 4.5rem;
  color: #fff;
  text-align: center;
  position: relative;
  z-index: 2;
  margin: 0;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.1;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  padding: 0 20px;

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 2.2rem;
  margin: 80px 0 40px;
  color: #2c3e50;
  position: relative;
  display: inline-block;
  font-weight: 700;
  letter-spacing: -0.5px;

  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 80px;
    height: 5px;
    background: linear-gradient(90deg, #635bff, #8a84ff);
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin: 60px 0 30px;
  }
`;

const Paragraph = styled.p`
  font-size: 1.15rem;
  line-height: 1.9;
  margin-bottom: 30px;
  color: #5a6a7e;
  // max-width: 800px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    line-height: 1.8;
  }
`;

const HighlightCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(99, 91, 255, 0.05),
    rgba(138, 132, 255, 0.05)
  );
  border-left: 4px solid #635bff;
  padding: 40px;
  border-radius: 0 16px 16px 0;
  margin: 50px 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, #635bff, #8a84ff);
  }

  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: #2c3e50;
    font-weight: 500;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
    margin: 40px 0;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 30px;
  margin: 70px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin: 50px 0;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border-top: 4px solid #635bff;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(99, 91, 255, 0.15);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #635bff, #8a84ff);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #635bff;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #635bff, #8a84ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #5a6a7e;
  font-weight: 500;
`;

const TeamSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
  margin-top: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const TeamMember = styled(motion.div)`
  text-align: center;
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const MemberImageContainer = styled.div`
  height: 280px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(to top, rgba(99, 91, 255, 0.3), transparent);
    z-index: 1;
  }

  img {
    transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const MemberInfo = styled.div`
  padding: 30px;
`;

const MemberName = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 8px;
  color: #2c3e50;
  font-weight: 700;
`;

const MemberRole = styled.p`
  color: #635bff;
  font-weight: 700;
  margin-bottom: 20px;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
`;

const MemberBio = styled.p`
  font-size: 1rem;
  color: #5a6a7e;
  line-height: 1.7;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #635bff, #8a84ff);
  color: white;
  border: none;
  padding: 18px 36px;
  font-size: 1.2rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 50px;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(99, 91, 255, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.6s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(99, 91, 255, 0.4);
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 16px;
  }
`;

const FloatingCircles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;

  span {
    position: absolute;
    display: block;
    border-radius: 50%;
    background: rgba(99, 91, 255, 0.05);
    animation: animate 25s linear infinite;
  }

  span:nth-of-type(1) {
    width: 300px;
    height: 300px;
    top: -150px;
    right: -150px;
  }

  span:nth-of-type(2) {
    width: 200px;
    height: 200px;
    bottom: 100px;
    left: -100px;
    animation-delay: 2s;
    animation-duration: 20s;
  }

  span:nth-of-type(3) {
    width: 150px;
    height: 150px;
    top: 30%;
    left: 20%;
    animation-delay: 4s;
    animation-duration: 15s;
  }

  @keyframes animate {
    0% {
      transform: translateY(0) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(-1000px) rotate(720deg);
      opacity: 0;
    }
  }
`;

export default function AboutUs() {
  return (
    <AboutContainer>
      <Header />
      <HeroSection background={slider1.src}>
        <Title>Building the Future of E-commerce Finance</Title>
      </HeroSection>

      <ContentContainer>
        <GradientAccent />
        <FloatingCircles>
          <span></span>
          <span></span>
          <span></span>
        </FloatingCircles>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Section>
            <Subtitle>Our Origin Story</Subtitle>
            <Paragraph>
              We were e-commerce sellers ourselves — juggling orders across
              Meesho, Amazon, and Flipkart — before we built Profit Pilot.
              Every month-end meant hours of manual work: downloading reports,
              stitching spreadsheets, and still never being sure if the number
              we saw was our real profit or just gross revenue.
            </Paragraph>
            <Paragraph>
              After talking to dozens of other sellers facing the same problem,
              we stopped waiting for a tool that understood Indian marketplace
              accounting and decided to build it ourselves.
            </Paragraph>
          </Section>

          <Section>
            <Subtitle>The Problem We Solved</Subtitle>
            <Paragraph>
              Most e-commerce sellers don&apos;t know their real profit until it&apos;s
              too late. Marketplace fees, return charges, GST deductions, and
              logistics costs silently eat into margins — and no single tool
              was showing the full picture for Indian sellers.
            </Paragraph>

            <StatsContainer>
              <StatItem>
                <StatNumber>42</StatNumber>
                <StatLabel>Sellers interviewed before building</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>3</StatNumber>
                <StatLabel>Marketplaces unified in one dashboard</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>0</StatNumber>
                <StatLabel>Spreadsheets needed after Profit Pilot</StatLabel>
              </StatItem>
            </StatsContainer>

            <HighlightCard>
              <p>
                &ldquo;We built the tool we always wished existed — one that
                shows your true net profit after every fee, charge, and
                return, not just what the marketplace paid you.&rdquo;
              </p>
            </HighlightCard>
          </Section>
          <Section>
            <Subtitle>Our Innovative Solution</Subtitle>
            <Paragraph>
              ProfitPilot was born from our deep understanding of e-commerce
              operations combined with cutting-edge technology. We built more
              than accounting software - we created a financial co-pilot that
              automates complex calculations, provides real-time insights, and
              surfaces opportunities for growth.
            </Paragraph>
            <Paragraph>
              Our platform eliminates guesswork, replacing it with clear,
              actionable financial intelligence tailored specifically for online
              sellers.
            </Paragraph>
          </Section>

          <Section>
            <Subtitle>The Power of Partnership</Subtitle>
            <Paragraph>
              The magic of ProfitPilot comes from the perfect balance of
              technical innovation and business strategy. While one of us
              obsesses over code quality, system architecture, and security, the
              other focuses on user experience, market fit, and growth strategy.
            </Paragraph>
            <Paragraph>
              This complementary partnership allows us to build a product
              that&rsquo;s not just technologically robust, but also intuitively
              designed for real business needs.
            </Paragraph>
          </Section>
          <Section>
            <Subtitle>Our Commitment to You</Subtitle>
            <Paragraph>
              We believe e-commerce entrepreneurs should spend their time
              growing their business, not wrestling with spreadsheets. Our
              commitment is to provide tools that give you back your most
              valuable resource - time.
            </Paragraph>
            <Paragraph>
              We&rsquo;re continuously evolving ProfitPilot based on user feedback,
              with exciting new features in development to serve the global
              e-commerce community.
            </Paragraph>
          </Section>
          <Section>
            <Subtitle>Join Our Journey</Subtitle>
            <Paragraph>
              This is just the beginning. As we grow, we remain committed to our
              core values: transparency, innovation, and putting sellers first.
              We&rsquo;re building more than software - we&rsquo;re building a movement to
              democratize financial intelligence for e-commerce.
            </Paragraph>

            {/* <Link href="/login">
              <CTAButton
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                Become Part of Our Story
              </CTAButton>
            </Link> */}
          </Section>

          {/* Uncomment when you have team images */}
          {/* <Section>
            <Subtitle>Meet The Visionaries</Subtitle>
            <TeamSection>
              <TeamMember
                whileHover={{ y: -10 }}
              >
                <MemberImageContainer>
                  <Image 
                    src={friend1} 
                    alt="Tech Founder" 
                    layout="fill"
                    objectFit="cover"
                  />
                </MemberImageContainer>
                <MemberInfo>
                  <MemberName>Rahul Sharma</MemberName>
                  <MemberRole>Chief Technology Officer</MemberRole>
                  <MemberBio>
                    With over a decade of experience in building scalable systems, Rahul architects the technical foundation of ProfitPilot. His passion for automation and data integrity ensures our platform is both powerful and reliable.
                  </MemberBio>
                </MemberInfo>
              </TeamMember>

              <TeamMember
                whileHover={{ y: -10 }}
              >
                <MemberImageContainer>
                  <Image 
                    src={friend2} 
                    alt="Concept Founder" 
                    layout="fill"
                    objectFit="cover"
                  />
                </MemberImageContainer>
                <MemberInfo>
                  <MemberName>Priya Patel</MemberName>
                  <MemberRole>Chief Strategy Officer</MemberRole>
                  <MemberBio>
                    Priya's deep understanding of e-commerce pain points shapes our product direction. Her research-driven approach ensures ProfitPilot solves real problems for real businesses.
                  </MemberBio>
                </MemberInfo>
              </TeamMember>
            </TeamSection>
          </Section> */}
        </motion.div>
      </ContentContainer>
      <Footer />
    </AboutContainer>
  );
}
